/*
 * SPDX-FileCopyrightText: 2021 CERN.
 * SPDX-FileCopyrightText: 2021 New York University.
 * SPDX-FileCopyrightText: 2022 data-futures.
 * SPDX-FileCopyrightText: 2023 Northwestern University.
 * SPDX-FileCopyrightText: 2024 KTH Royal Institute of Technology.
 * SPDX-License-Identifier: MIT
 */

import _get from "lodash/get";
import React from "react";
import { i18next } from "@translations/invenio_app_rdm/i18next";
import { DateTime } from "luxon";

export function SearchItemCreators({ creators, className, othersLink }) {
  let spanClass = "creatibutor-wrap separated";
  className && (spanClass += ` ${className}`);

  function makeIcon(scheme, identifier, name) {
    let link = null;
    let linkTitle = null;
    let icon = null;
    let alt = "";
    let id = "";

    switch (scheme) {
      case "orcid":
        link = `https://orcid.org/${identifier}`;
        linkTitle = i18next.t("ORCID profile");
        icon = "/static/images/orcid.svg";
        alt = "ORCID logo";
        break;
      case "ror":
        link = `https://ror.org/${identifier}`;
        linkTitle = i18next.t("ROR profile");
        icon = "/static/images/ror-icon.svg";
        alt = "ROR logo";
        break;
      case "gnd":
        link = `https://d-nb.info/gnd/${identifier}`;
        linkTitle = i18next.t("GND profile");
        icon = "/static/images/gnd-icon.svg";
        alt = "GND logo";
        break;
      case "huggingface":
        id = `${identifier}`.split(":");
        link = `https://huggingface.co/${id[1]}`;
        linkTitle = i18next.t("huggingface profile");
        icon = "/static/images/huggingface-icon.svg";
        alt = "huggingface logo";
        break;
      case "enka":
        id = `${identifier}`.split(":");
        link = `https://enka.network/u/${id[1]}`;
        linkTitle = i18next.t("enka profile");
        icon = "/static/images/enka-icon.svg";
        alt = "enka logo";
        break;
      case "youtube":
        id = `${identifier}`.split(":");
        link = `https://www.youtube.com/${id[1]}`;
        linkTitle = i18next.t("youtube profile");
        icon = "/static/images/youtube-icon.svg";
        alt = "youtube logo";
        break;
      case "akasha":
        id = `${identifier}`.split(":");
        link = `https://akasha.cv/profile/${id[1]}`;
        linkTitle = i18next.t("akasha profile");
        icon = "/static/images/akasha-icon.svg";
        alt = "akasha logo";
        break;
      case "kaggle":
        id = `${identifier}`.split(":");
        link = `https://www.kaggle.com/${id[1]}`;
        linkTitle = i18next.t("kaggle profile");
        icon = "/static/images/kaggle-icon.svg";
        alt = "kaggle logo";
        break;
      case "discord":
        id = `${identifier}`.split(":");
        link = `https://discord.gg/${id[1]}`;
        linkTitle = i18next.t("discord profile");
        icon = "/static/images/discord-icon.svg";
        alt = "discord logo";
        break;
      case "github":
        id = `${identifier}`.split(":");
        link = `https://github.com/${id[1]}`;
        linkTitle = i18next.t("github profile");
        icon = "/static/images/github-icon.svg";
        alt = "github logo";
        break;
      case "tiktok":
        id = `${identifier}`.split(":");
        link = `https://tiktok.com/${id[1]}`;
        linkTitle = i18next.t("tiktok profile");
        icon = "/static/images/tiktok-icon.svg";
        alt = "tiktok logo";
        break;
      case "twitter":
        id = `${identifier}`.split(":");
        link = `https://x.com/${id[1]}`;
        linkTitle = i18next.t("twitter profile");
        icon = "/static/images/twitter-icon.svg";
        alt = "twitter logo";
        break;
      case "instagram":
        id = `${identifier}`.split(":");
        link = `https://instagram.com/${id[1]}`;
        linkTitle = i18next.t("instagram profile");
        icon = "/static/images/instagram-icon.svg";
        alt = "instagram logo";
        break;
      case "facebook":
        id = `${identifier}`.split(":");
        link = `https://facebook.com/${id[1]}`;
        linkTitle = i18next.t("facebook profile");
        icon = "/static/images/facebook-icon.svg";
        alt = "facebook logo";
        break;
      default:
        return null;
    }

    icon = (
      <a
        className="no-text-decoration mr-0"
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${name}: ${linkTitle}`}
        title={`${name}: ${linkTitle}`}
        key={scheme}
      >
        <img className="inline-id-icon ml-5" src={icon} alt={alt} />
      </a>
    );
    return icon;
  }

  function getIcons(creator) {
    let ids = _get(creator, "person_or_org.identifiers", []);
    let creatorName = _get(creator, "person_or_org.name", i18next.t("No name"));
    let icons = ids.map((c) => makeIcon(c.scheme, c.identifier, creatorName));
    return icons;
  }

  function getLink(creator) {
    let creatorName = _get(creator, "person_or_org.name", i18next.t("No name"));
    let link = (
      <a
        className="creatibutor-link"
        href={`/search?q=metadata.creators.person_or_org.name:"${creatorName}"`}
        title={`${creatorName}: ${i18next.t("Search")}`}
      >
        <span className="creatibutor-name">{creatorName}</span>
      </a>
    );
    return link;
  }

  const numDisplayed = 10;
  const result = creators.slice(0, numDisplayed).map((creator) => (
    <span className={spanClass} key={creator.person_or_org.name}>
      {getLink(creator)}
      {getIcons(creator)}
    </span>
  ));

  const numExtra = creators.length - numDisplayed;
  if (0 < numExtra) {
    let text;
    if (numExtra === 1) {
      text = i18next.t("and {{count}} other", { count: numExtra });
    } else {
      text = i18next.t("and {{count}} others", { count: numExtra });
    }
    result.push(
      <span className={spanClass} key={text}>
        <a className="creatibutor-link" href={othersLink}>
          <span className="creatibutor-name">
            {i18next.t(text, { count: numExtra })}
          </span>
        </a>
      </span>
    );
  }

  return result;
}


export function SearchItemContributors({ contributors, className, othersLink }) {
  let spanClass = "creatibutor-wrap separated";
  className && (spanClass += ` ${className}`);

  function makeIcon(scheme, identifier, name) {
    let link = null;
    let linkTitle = null;
    let icon = null;
    let alt = "";
    let id = "";

    switch (scheme) {
      case "orcid":
        link = `https://orcid.org/${identifier}`;
        linkTitle = i18next.t("ORCID profile");
        icon = "/static/images/orcid.svg";
        alt = "ORCID logo";
        break;
      case "ror":
        link = `https://ror.org/${identifier}`;
        linkTitle = i18next.t("ROR profile");
        icon = "/static/images/ror-icon.svg";
        alt = "ROR logo";
        break;
      case "gnd":
        link = `https://d-nb.info/gnd/${identifier}`;
        linkTitle = i18next.t("GND profile");
        icon = "/static/images/gnd-icon.svg";
        alt = "GND logo";
        break;
      case "huggingface":
        id = `${identifier}`.split(":");
        link = `https://huggingface.co/${id[1]}`;
        linkTitle = i18next.t("huggingface profile");
        icon = "/static/images/huggingface-icon.svg";
        alt = "huggingface logo";
        break;
      case "enka":
        id = `${identifier}`.split(":");
        link = `https://enka.network/u/${id[1]}`;
        linkTitle = i18next.t("enka profile");
        icon = "/static/images/enka-icon.svg";
        alt = "enka logo";
        break;
      case "youtube":
        id = `${identifier}`.split(":");
        link = `https://www.youtube.com/${id[1]}`;
        linkTitle = i18next.t("youtube profile");
        icon = "/static/images/youtube-icon.svg";
        alt = "youtube logo";
        break;
      case "akasha":
        id = `${identifier}`.split(":");
        link = `https://akasha.cv/profile/${id[1]}`;
        linkTitle = i18next.t("akasha profile");
        icon = "/static/images/akasha-icon.svg";
        alt = "akasha logo";
        break;
      case "kaggle":
        id = `${identifier}`.split(":");
        link = `https://www.kaggle.com/${id[1]}`;
        linkTitle = i18next.t("kaggle profile");
        icon = "/static/images/kaggle-icon.svg";
        alt = "kaggle logo";
        break;
      case "discord":
        id = `${identifier}`.split(":");
        link = `https://discord.gg/${id[1]}`;
        linkTitle = i18next.t("discord profile");
        icon = "/static/images/discord-icon.svg";
        alt = "discord logo";
        break;
      case "github":
        id = `${identifier}`.split(":");
        link = `https://github.com/${id[1]}`;
        linkTitle = i18next.t("github profile");
        icon = "/static/images/github-icon.svg";
        alt = "github logo";
        break;
      case "tiktok":
        id = `${identifier}`.split(":");
        link = `https://tiktok.com/${id[1]}`;
        linkTitle = i18next.t("tiktok profile");
        icon = "/static/images/tiktok-icon.svg";
        alt = "tiktok logo";
        break;
      case "twitter":
        id = `${identifier}`.split(":");
        link = `https://x.com/${id[1]}`;
        linkTitle = i18next.t("twitter profile");
        icon = "/static/images/twitter-icon.svg";
        alt = "twitter logo";
        break;
      case "instagram":
        id = `${identifier}`.split(":");
        link = `https://instagram.com/${id[1]}`;
        linkTitle = i18next.t("instagram profile");
        icon = "/static/images/instagram-icon.svg";
        alt = "instagram logo";
        break;
      case "facebook":
        id = `${identifier}`.split(":");
        link = `https://facebook.com/${id[1]}`;
        linkTitle = i18next.t("facebook profile");
        icon = "/static/images/facebook-icon.svg";
        alt = "facebook logo";
        break;
      default:
        return null;
    }

    icon = (
      <a
        className="no-text-decoration mr-0"
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${name}: ${linkTitle}`}
        title={`${name}: ${linkTitle}`}
        key={scheme}
      >
        <img className="inline-id-icon ml-5" src={icon} alt={alt} />
      </a>
    );
    return icon;
  }

  function getIcons(contributor) {
    let ids = _get(contributor, "person_or_org.identifiers", []);
    let contributorName = _get(contributor, "person_or_org.name", i18next.t("No name"));
    let icons = ids.map((c) => makeIcon(c.scheme, c.identifier, contributorName));
    return icons;
  }

  function getLink(contributor) {
    let contributorName = _get(contributor, "person_or_org.name", i18next.t("No name"));
    let link = (
      <a
        className="creatibutor-link"
        href={`/search?q=metadata.contributors.person_or_org.name:"${contributorName}"`}
        title={`${contributorName}: ${i18next.t("Search")}`}
      >
        <span className="creatibutor-name">{contributorName}</span>
      </a>
    );
    return link;
  }

  const numDisplayed = 10;
  const result = contributors.slice(0, numDisplayed).map((contributor) => (
    <span className={spanClass} key={contributor.person_or_org.name}>
      {getLink(contributor)}
      {getIcons(contributor)}
    </span>
  ));

  const numExtra = contributors.length - numDisplayed;
  if (0 < numExtra) {
    let text;
    if (numExtra === 1) {
      text = i18next.t("and {{count}} other", { count: numExtra });
    } else {
      text = i18next.t("and {{count}} others", { count: numExtra });
    }
    result.push(
      <span className={spanClass} key={text}>
        <a className="creatibutor-link" href={othersLink}>
          <span className="creatibutor-name">
            {i18next.t(text, { count: numExtra })}
          </span>
        </a>
      </span>
    );
  }

  return result;
}

/**
 * Returns a human readable timestamp in the format "4 days ago".
 *
 * @param {Date} timestamp
 * @returns string
 */
export const timestampToRelativeTime = (timestamp) =>
  DateTime.fromISO(timestamp).setLocale(i18next.language).toRelative();

/**
 * Returns a string-formatted number in the user's locale (e.g. 1,000 in en-GB but 1.000 in de-DE)
 *
 * @param {Number} number
 * @returns string
 */
export const localizedFormatNumber = (number) =>
  new Intl.NumberFormat(i18next.language).format(number);
